import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { ApiResponse } from 'src/app/models/api-response.model';
import { environment } from 'src/environments/environment';

// The API signs photo links for an hour; refresh well before they expire.
const CACHE_TTL_MS = 50 * 60 * 1000;
const MAX_BATCH = 100;

@Injectable({
  providedIn: 'root',
})
export class ProfilePhotoService {
  private readonly cache = new Map<string, { url: string | null; expiresAt: number }>();
  private readonly pending = new Map<string, ReplaySubject<string | null>>();
  private queue = new Set<string>();
  private flushScheduled = false;

  constructor(private http: HttpClient) {}

  /** A displayable link for a user's profile photo; requests made in the same tick share one API call. */
  url$(userId: string | null | undefined): Observable<string | null> {
    if (!userId) {
      return of(null);
    }
    const cached = this.cache.get(userId);
    if (cached && cached.expiresAt > Date.now()) {
      return of(cached.url);
    }
    let subject = this.pending.get(userId);
    if (!subject) {
      subject = new ReplaySubject<string | null>(1);
      this.pending.set(userId, subject);
      this.queue.add(userId);
      this.scheduleFlush();
    }
    return subject.asObservable();
  }

  /** Forgets a cached link, e.g. right after the user uploaded a new photo. */
  invalidate(userId: string): void {
    this.cache.delete(userId);
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) {
      return;
    }
    this.flushScheduled = true;
    setTimeout(() => this.flush());
  }

  private flush(): void {
    this.flushScheduled = false;
    const ids = [...this.queue];
    this.queue = new Set<string>();
    for (let i = 0; i < ids.length; i += MAX_BATCH) {
      const batch = ids.slice(i, i + MAX_BATCH);
      this.http
        .post<ApiResponse<Record<string, string | null>>>(`${environment.welbyEndpoint}/api/v1/users/profile-photos/urls`, { userIds: batch })
        .subscribe({
          next: (res) => batch.forEach((id) => this.resolve(id, res?.data?.[id] ?? null, true)),
          // A failed lookup shows the default avatar now and is retried on the next request.
          error: () => batch.forEach((id) => this.resolve(id, null, false)),
        });
    }
  }

  private resolve(userId: string, url: string | null, cache: boolean): void {
    if (cache) {
      this.cache.set(userId, { url, expiresAt: Date.now() + CACHE_TTL_MS });
    }
    const subject = this.pending.get(userId);
    this.pending.delete(userId);
    subject?.next(url);
    subject?.complete();
  }
}
